import { create } from 'zustand'
import type { ThemeStore, ThemePreference, EffectiveTheme } from '@/types'

export const THEME_KEY = 'tmc-theme-preference'
const isBrowser = typeof window !== 'undefined'

function getSystemPreference(): EffectiveTheme {
  if (!isBrowser) return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function getEffectiveTheme(preference: ThemePreference): EffectiveTheme {
  if (preference === 'auto') return getSystemPreference()
  return preference
}

function applyTheme(effective: EffectiveTheme): void {
  if (!isBrowser) return
  const root = document.documentElement
  root.setAttribute('data-theme', effective)
  root.style.colorScheme = effective
}

function getStoredPreference(): ThemePreference {
  if (!isBrowser) return 'auto'
  try {
    return (localStorage.getItem(THEME_KEY) as ThemePreference) || 'auto'
  } catch {
    return 'auto'
  }
}

const storedPref = getStoredPreference()

export const useThemeStore = create<ThemeStore>((set, get) => ({
  preference: storedPref,
  effective: getEffectiveTheme(storedPref),

  setPreference: (preference: ThemePreference) => {
    const effective = getEffectiveTheme(preference)
    try { localStorage.setItem(THEME_KEY, preference) } catch {}
    applyTheme(effective)
    set({ preference, effective })
  },

  _onSystemChange: () => {
    const { preference } = get()
    if (preference === 'auto') {
      const effective = getSystemPreference()
      applyTheme(effective)
      set({ effective })
    }
  },
}))

// Listen for OS changes (browser only). Initial theme is applied by the inline script in root.tsx.
if (isBrowser) {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    useThemeStore.getState()._onSystemChange()
  })
}
